/**
 * Self-contained on purpose. This module dispatches the `cart-open` event the
 * Alpine drawer listens for, so it MUST NOT depend on any ES import: a relative
 * import that fails to resolve on Shopify's CDN aborts the whole module, which
 * would stop `customElements.define` and leave the drawer unable to open.
 * The /cart.js reads below are deduped by the global fetch patch in theme.liquid.
 */

function isFullCart(cart) {
  return !!(cart && Array.isArray(cart.items) && typeof cart.total_price === 'number');
}

function cacheCart(cart) {
  if (isFullCart(cart) && window.__cartJsCache?.set) {
    window.__cartJsCache.set(cart);
  }
}

let _inflightCart = null;
function fetchCart() {
  if (_inflightCart) return _inflightCart;
  const root = window.Shopify?.routes?.root || '/';
  _inflightCart = fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
    .then((res) => res.json())
    .then((cart) => {
      cacheCart(cart);
      return cart;
    })
    .finally(() => {
      _inflightCart = null;
    });
  return _inflightCart;
}

// Shopify's /cart/add.js response has no `token`, so the cart engine never has
// the full cart on an add — leaving the header count binder and the
// html.js-ajax-cart-empty class stale. Sync the header count + empty-state
// classes from the authoritative cart.
let _cartChromeRecovering = false;

function syncCartChrome(cart) {
  if (!isFullCart(cart)) return;
  const count = cart.item_count || 0;

  document
    .querySelectorAll('[data-cart-count], [data-ajax-cart-bind="item_count"]')
    .forEach((el) => {
      el.textContent = count;
      el.classList.toggle('showc', count > 0);
      el.classList.toggle('hidec', count === 0);
    });

  const html = document.documentElement;
  const drawerHasItems = !!document.querySelector('cart-drawer .cart-item');
  const isEmpty = drawerHasItems ? false : count === 0;
  html.classList.toggle('js-ajax-cart-empty', isEmpty);
  html.classList.toggle('js-ajax-cart-not-empty', !isEmpty);
}

// Re-render ONLY the cart-drawer section via the Section Rendering API (a GET),
// then swap its [data-ajax-cart-section] body in place. Used to recover a drawer
// that rendered empty without paying for a POST /cart/update.js write (which is
// serialized behind Shopify's per-session cart lock — the slow `update.js`).
function refreshDrawerSection() {
  const sectionEl = document
    .querySelector('cart-drawer')
    ?.closest('[id^="shopify-section-"]');
  if (!sectionEl) return Promise.resolve(false);

  const sectionId = sectionEl.id.replace('shopify-section-', '');

  return fetch(`${window.location.pathname}?sections=${encodeURIComponent(sectionId)}`)
    .then((res) => res.json())
    .then((data) => {
      const html = data?.[sectionId];
      if (!html) return false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-ajax-cart-section]');
      const live = sectionEl.querySelector('[data-ajax-cart-section]');
      if (fresh && live) {
        live.innerHTML = fresh.innerHTML;
        return true;
      }
      return false;
    })
    .catch(() => false);
}

// The drawer opened showing its page-load empty state because the cart-drawer
// section wasn't re-rendered (its [data-ajax-cart-section] wasn't in the add
// request at request-start). Only when the drawer genuinely shows empty while
// the cart has items do we re-render it — via a GET section render, not a cart
// write — so the happy path (drawer already re-rendered) costs nothing.
function recoverEmptyDrawer(cart) {
  if (_cartChromeRecovering) return;
  if (!isFullCart(cart) || (cart.item_count || 0) === 0) return;

  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;

  const showsEmpty = !!drawer.querySelector('.cart-drawer__empty');
  const hasItems = !!drawer.querySelector('.cart-items .cart-item');
  if (!showsEmpty && hasItems) return;

  _cartChromeRecovering = true;
  refreshDrawerSection().finally(() => {
    _cartChromeRecovering = false;
  });
}

export class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.onCartRequestEnd = this.onCartRequestEnd.bind(this);
    this.onBundleRemove = this.onBundleRemove.bind(this);
  }

  connectedCallback() {
    document.addEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
    this.addEventListener('click', this.onBundleRemove);
  }

  disconnectedCallback() {
    document.removeEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
    this.removeEventListener('click', this.onBundleRemove);
  }

  onCartRequestEnd(event) {
    const { requestState } = event.detail || {};
    if (!requestState?.responseData?.ok) return;

    const isAdd = requestState.requestType === 'add';
    if (isAdd) {
      window.dispatchEvent(new CustomEvent('cart-open'));
    }

    requestAnimationFrame(() => {
      const scrollEl = this.querySelector('.cart-items');
      if (scrollEl) scrollEl.scrollTo({ top: 0, behavior: 'auto' });
    });

    // Resolve the authoritative full cart, then sync header chrome. The add
    // response is partial, so fall back to a deduped /cart.js for the real
    // item_count (this also refreshes the shared cart cache).
    const body = requestState.responseData.body;
    const cartPromise = isFullCart(body) ? Promise.resolve(body) : fetchCart();

    cartPromise
      .then((cart) => {
        cacheCart(cart);
        syncCartChrome(cart);
        if (isAdd) recoverEmptyDrawer(cart);
      })
      .catch(() => {});
  }

  onBundleRemove(event) {
    const btn = event.target.closest('.js-cart-bundle-remove');
    if (!btn) return;

    event.preventDefault();
    const keysStr = btn.dataset.bundleKeys;
    if (!keysStr) return;

    const keys = keysStr.split(',').filter(Boolean);
    if (!keys.length) return;

    btn.disabled = true;
    btn.textContent = 'Removing...';

    const baseUpdates = {};
    keys.forEach(key => { baseUpdates[key] = 0; });

    // Drop this bundle's share of the hidden free gift line in the SAME
    // update.js call — no trailing second request. The remaining quantity is
    // pre-computed in Liquid (other sets in the cart keep their share);
    // component-cart-free-gift.js reconciles any drift afterwards. If the
    // gift key went stale (line already gone) the call is retried once
    // without it so the set removal itself never fails.
    const giftKey = btn.dataset.giftKey;
    const updates = { ...baseUpdates };
    if (giftKey) {
      updates[giftKey] = Math.max(0, parseInt(btn.dataset.giftRemainingQty, 10) || 0);
    }

    if (window.liquidAjaxCart?.update) {
      const runUpdate = (upd, canRetry) => {
        window.liquidAjaxCart.update(
          { updates: upd },
          {
            lastCallback: (requestState) => {
              if (!requestState?.responseData?.ok && canRetry) {
                runUpdate(baseUpdates, false);
                return;
              }
              btn.disabled = false;
              btn.textContent = 'Remove';
            }
          }
        );
      };
      runUpdate(updates, !!giftKey);
    } else {
      const runUpdateRaw = (upd, canRetry) => {
        fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: upd })
        })
        .then(res => {
          if (!res.ok && canRetry) return runUpdateRaw(baseUpdates, false);
          return res.json().then(() => { window.location.reload(); });
        })
        .catch(err => {
          console.error('Bundle remove error:', err);
          btn.disabled = false;
          btn.textContent = 'Remove';
        });
      };
      runUpdateRaw(updates, !!giftKey);
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

// Keep the header count in sync when the drawer is opened (e.g. from the header
// icon) without a preceding cart mutation.
window.addEventListener('cart-open', () => {
  fetchCart()
    .then((cart) => syncCartChrome(cart))
    .catch(() => {});
});
